// Modification : Zacharie Nolet
// Date de modification : 2026-05-07
// Commit : 38f83d1 - Reconfiguration acceuil
import React, { useEffect, useState } from "react";
import { Box, Typography, Avatar, CircularProgress } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import axios from "../../../utils/Api";

const QUOTES = [
  { text: "L'éducation est l'arme la plus puissante que vous puissiez utiliser pour changer le monde.", author: "Nelson Mandela" },
  { text: "Vivre sans apprendre, c'est mourir sans avoir vécu.", author: "Molière" },
  { text: "La racine de l'éducation est amère, mais son fruit est doux.", author: "Aristote" },
  { text: "L'intelligence sans ambition est un oiseau sans ailes.", author: "Salvador Dalí" },
  { text: "On n'apprend pas pour l'école, mais pour la vie.", author: "Sénèque" },
  { text: "La connaissance s'acquiert par l'expérience, tout le reste n'est qu'information.", author: "Albert Einstein" },
  { text: "L'éducation est ce qui reste après que l'on a oublié ce que l'on a appris à l'école.", author: "B. F. Skinner" },
  { text: "Apprendre sans réfléchir est vain. Réfléchir sans apprendre est dangereux.", author: "Confucius" },
  { text: "L'intelligence plus le caractère, voilà le véritable but de l'éducation.", author: "Martin Luther King Jr." },
  { text: "L'éducation n'est pas une préparation à la vie; l'éducation est la vie elle-même.", author: "John Dewey" },
  { text: "L'éducation est donc un processus de vie, et non une préparation à une vie future.", author: "John Dewey" },
  { text: "Un investissement dans la connaissance rapporte toujours les meilleurs intérêts.", author: "Benjamin Franklin" },
  { text: "Dis-moi et j'oublierai. Enseigne-moi et je me souviendrai. Implique-moi et j'apprendrai.", author: "Benjamin Franklin" },
  { text: "Il est préférable d'apprendre tard que de ne jamais apprendre.", author: "Publilius Syrus" },
  { text: "Les choses enseignées dans les écoles et les collèges ne sont pas une éducation, mais les moyens de l'éducation.", author: "Ralph Waldo Emerson" },
  { text: "L'esprit n'est pas un vase à remplir, mais un feu à allumer.", author: "Plutarque" },
  { text: "La sagesse commence dans l'émerveillement.", author: "Socrate" },
  { text: "La seule vraie sagesse est de savoir que l'on ne sait rien.", author: "Socrate" },
  { text: "Le savoir, c'est le pouvoir.", author: "Francis Bacon" },
  { text: "Celui qui ouvre une porte d'école ferme une prison.", author: "Victor Hugo" },
  { text: "L'éducation développe les facultés, mais ne les crée pas.", author: "Voltaire" },
  { text: "Le but de l'éducation est de remplacer un esprit vide par un esprit ouvert.", author: "Malcolm Forbes" },
  { text: "L'éducation est la clé pour ouvrir la porte dorée de la liberté.", author: "George Washington Carver" },
  { text: "L'apprentissage n'épuise jamais l'esprit.", author: "Léonard de Vinci" },
  { text: "Étudier sans désir gâte la mémoire, et elle ne retient rien de ce qu'elle reçoit.", author: "Léonard de Vinci" },
  { text: "Je ne perds jamais. Soit je gagne, soit j'apprends.", author: "Nelson Mandela" },
  { text: "Le succès, c'est d'aller d'échec en échec sans perdre son enthousiasme.", author: "Winston Churchill" },
  { text: "Le succès dépend de la préparation préalable, et sans cette préparation il y aura certainement un échec.", author: "Confucius" },
];

export default function StudentHomeFallback({ user }) {
  const [stats, setStats]           = useState(null);
  const [loadingStats, setLoading]  = useState(true);
  const [quoteIndex, setQuoteIndex] = useState(() => Math.floor(Math.random() * QUOTES.length));
  const [visible, setVisible]       = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      axios.get("/user/my-badges").catch(() => ({ data: [] })),
      axios.get("/stats/leaderboard").catch(() => ({ data: [] })),
    ]).then(([badgesRes, lbRes]) => {
      const myBadges   = badgesRes.data || [];
      const leaderboard = lbRes.data    || [];
      const myRank = leaderboard.findIndex(
        (u) => u.id === user.id || u.username === user.username
      ) + 1;
      setStats({ badgeCount: myBadges.length, rank: myRank > 0 ? myRank : null, total: leaderboard.length });
      setLoading(false);
    });
  }, [user]);

  useEffect(() => {
    const t = setInterval(() => {
      setVisible(false);
      setTimeout(() => { setQuoteIndex(i => (i + 1) % QUOTES.length); setVisible(true); }, 400);
    }, 12000);
    return () => clearInterval(t);
  }, []);

  const quote = QUOTES[quoteIndex];

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", pb: 6 }}>

      {/* ── Hero avec gradient du site ─────────────────────────────────── */}
      <Box sx={{
        background: "linear-gradient(90deg, #1976d2, #1565c0)",
        borderRadius: 3,
        p: { xs: 3, sm: 5 },
        mb: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
        boxShadow: "0 4px 18px rgba(0,0,0,0.18)",
      }}>
<Box
  sx={{
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 1,
  }}
>
  <Box>
    <Typography
      sx={{
        fontWeight: 800,
        letterSpacing: ".18rem",
        color: "white",
        fontSize: { xs: "1.85rem", sm: "2.45rem" },
        lineHeight: 1,
      }}
    >
      E-BADGE
    </Typography>
  </Box>
</Box>

        {/* Avatar + prénom */}
        <Avatar
          sx={{
            width: 72, height: 72,
            bgcolor: "rgba(255,255,255,0.25)",
            border: "3px solid rgba(255,255,255,0.6)",
            fontSize: "1.8rem",
            fontWeight: 700,
            color: "white",
          }}
        >
          {user?.first_name?.charAt(0)?.toUpperCase() || "É"}
        </Avatar>
      </Box>

      {/* ── Statistiques ───────────────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>

        <Box sx={{
          bgcolor: "white",
          border: "1px solid rgba(25,118,210,0.2)",
          borderRadius: 2,
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          boxShadow: "0 2px 8px rgba(25,118,210,0.08)",
        }}>
          <WorkspacePremiumIcon sx={{ color: "#1976d2", fontSize: 32, mb: 0.5 }} />
          <Typography sx={{ fontSize: "2rem", fontWeight: 700, color: "#1976d2", lineHeight: 1 }}>
            {loadingStats ? <CircularProgress size={24} /> : stats?.badgeCount ?? 0}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Badges obtenus
          </Typography>
        </Box>

        <Box sx={{
          bgcolor: "white",
          border: "1px solid rgba(25,118,210,0.2)",
          borderRadius: 2,
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          boxShadow: "0 2px 8px rgba(25,118,210,0.08)",
        }}>
          <EmojiEventsIcon sx={{ color: "#1976d2", fontSize: 32, mb: 0.5 }} />
          <Typography sx={{ fontSize: "2rem", fontWeight: 700, color: "#1976d2", lineHeight: 1 }}>
            {loadingStats
              ? <CircularProgress size={24} />
              : stats?.rank ? `#${stats.rank}` : "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {stats?.total ? `Classement / ${stats.total}` : "Classement"}
          </Typography>
        </Box>
      </Box>

      {/* ── Citation ───────────────────────────────────────────────────── */}
      <Box sx={{
        bgcolor: "white",
        border: "1px solid rgba(25,118,210,0.15)",
        borderLeft: "4px solid #1976d2",
        borderRadius: "0 12px 12px 0",
        p: 3,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.4s ease",
        boxShadow: "0 2px 8px rgba(25,118,210,0.08)",
      }}>
        <Typography
          sx={{ fontStyle: "italic", fontSize: "1rem", lineHeight: 1.7, color: "text.primary", mb: 1 }}
        >
          « {quote.text} »
        </Typography>
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#1976d2" }}>
          — {quote.author}
        </Typography>
      </Box>

    </Box>
  );
}
